import { getCommentarySystem } from '@/game/commentarySystem';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const Commentary: React.FC = () => {
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  const [currentCommentary, setCurrentCommentary] = useState<{
    message: string;
    style: string;
  } | null>(null);

  useEffect(() => {
    let host = document.getElementById('game-toast-portal');
    let didCreateHost = false;

    if (!host) {
      host = document.createElement('div');
      host.id = 'game-toast-portal';
      document.body.appendChild(host);
      didCreateHost = true;
    }

    setPortalHost(host);

    return () => {
      setPortalHost(null);
      if (didCreateHost && host) {
        host.remove();
      }
    };
  }, []);

  useEffect(() => {
    const commentarySystem = getCommentarySystem();

    const unsubscribe = commentarySystem.onCommentary(event => {
      if (event) {
        setCurrentCommentary({
          message: event.message,
          style: event.style || 'normal',
        });
      } else {
        setCurrentCommentary(null);
      }
    });

    return unsubscribe;
  }, []);

  const getStyleClasses = (style: string) => {
    switch (style) {
      case 'excited':
        return 'commentary-excited';
      case 'warning':
        return 'commentary-warning';
      case 'achievement':
        return 'commentary-achievement';
      default:
        return 'commentary-normal';
    }
  };

  if (!portalHost) {
    return null;
  }

  return createPortal(
    <div className="commentary-container">
      <AnimatePresence>
        {currentCommentary && (
          <motion.div
            className={`commentary-bubble ${getStyleClasses(currentCommentary.style)}`}
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.8 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25,
            }}
          >
            <div className="commentary-text">{currentCommentary.message}</div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .commentary-container {
          position: fixed;
          top: var(--game-toast-top, calc(60px + env(safe-area-inset-top, 0px)));
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          padding: 0 10px;
          z-index: 9999;
          pointer-events: none;
        }

        .commentary-bubble {
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 12px 20px;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.5px;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.15);
          text-align: center;
          max-width: min(92vw, 460px);
        }

        .commentary-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .commentary-text {
          display: inline-block;
          text-align: center;
          line-height: 1.4;
          white-space: normal;
        }

        .commentary-normal {
          background: rgba(0, 0, 0, 0.9);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .commentary-excited {
          background: rgba(33, 150, 243, 0.9);
          border-color: rgba(144, 202, 249, 0.5);
          animation: commentary-pulse 1s ease-in-out infinite;
        }

        .commentary-warning {
          background: rgba(255, 87, 34, 0.9);
          border-color: rgba(255, 138, 101, 0.5);
        }

        .commentary-achievement {
          background: linear-gradient(135deg, rgba(76, 175, 80, 0.9), rgba(129, 199, 132, 0.9));
          border-color: rgba(178, 223, 219, 0.5);
          box-shadow: 0 4px 20px rgba(76, 175, 80, 0.6);
        }

        @keyframes commentary-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @media (max-width: 768px) {
          .commentary-container {
            max-width: 100vw;
          }

          .commentary-bubble {
            padding: 10px 16px;
            font-size: 13px;
            max-width: min(94vw, 360px);
          }

          .commentary-icon {
            font-size: 18px;
          }
        }
      `}</style>
    </div>,
    portalHost
  );
};

export default Commentary;
